import { readonly, ref, shallowReadonly, shallowRef } from 'vue'

import type {
  AgentRouter,
  ChatOrigin,
  PromptTemplate,
} from '@onxmaps/ai-toolkit'

export interface AiChatCardConfig {
  title?: string
  router?: AgentRouter
  event?: {
    origin?: ChatOrigin
    first_result_type?: string
    starter_chip?: string
  }
  /**
   * When true, openAiChatCard resets the conversation even if the card is
   * already open. Defaults to false so re-opening preserves the in-progress
   * conversation. Not stored on the card state.
   */
  clearConversation?: boolean
}

const config = shallowRef<AiChatCardConfig>({})
const isOpen = ref(false)
const pendingPrompt = shallowRef<PromptTemplate | null>(null)
const pendingMessage = shallowRef<string | null>(null)
// Bumped when an already-open card is re-opened with `clearConversation` or a
// different `router` reference. control-ai-chat watches this to start a fresh
// conversation.
// sendPrompt/sendMessage never bump it, so a reopen, refresh, or query-card
// prompt keeps the current conversation.
const conversationKey = ref(0)
// Incremented only when the card transitions from closed to open.
const openEventKey = ref(0)
// Incremented on every openAiChatCard call (open or already-open).
// control-ai-chat watches this to expand the card so a collapsed card pops
// back up when the user clicks an opener.
const expandKey = ref(0)

/**
 * State is module-scoped because there is one global AI chat card. `sendPrompt`
 * preserves existing config when the card is already open so contextual
 * openers (e.g. land analysis) are not clobbered by a downstream prompt.
 */
export function useAiChatCard() {
  function openAiChatCard(next: AiChatCardConfig = {}): void {
    const wasOpen = isOpen.value
    const previousRouter = config.value.router
    const { clearConversation, ...stored } = next
    config.value = {
      ...stored,
      event: {
        origin: 'navigation menu',
        starter_chip: 'none',
        ...stored.event,
      },
    }
    isOpen.value = true
    expandKey.value++
    if (!wasOpen) {
      openEventKey.value++
    }
    if (clearConversation || (wasOpen && stored.router !== previousRouter)) {
      // Router change implies a different agent context, so reset the
      // conversation (and the cached agent in control-ai-chat).
      conversationKey.value++
    }
  }

  function sendPrompt(
    prompt: PromptTemplate,
    overrides?: Partial<Omit<AiChatCardConfig, 'clearConversation'>>
  ): void {
    if (!prompt.canPrompt(null)) {
      return
    }
    config.value = {
      ...config.value,
      ...overrides,
      event: {
        origin: 'navigation menu',
        ...config.value.event,
        ...overrides?.event,
        starter_chip: overrides?.event?.starter_chip ?? prompt.title,
      },
    }
    if (!isOpen.value) {
      isOpen.value = true
      openEventKey.value++
    }
    pendingMessage.value = null
    pendingPrompt.value = prompt
  }

  function consumePendingPrompt(): void {
    pendingPrompt.value = null
  }

  function sendMessage(
    text: string,
    overrides?: Partial<Omit<AiChatCardConfig, 'clearConversation'>>
  ): void {
    const trimmed = text.trim()
    if (!trimmed) {
      return
    }
    config.value = {
      ...config.value,
      ...overrides,
      event: {
        origin: 'navigation menu',
        ...config.value.event,
        ...overrides?.event,
        starter_chip: overrides?.event?.starter_chip ?? 'none',
      },
    }
    if (!isOpen.value) {
      isOpen.value = true
      openEventKey.value++
    }
    pendingPrompt.value = null
    pendingMessage.value = trimmed
  }

  function consumePendingMessage(): void {
    pendingMessage.value = null
  }

  function closeAiChatCard(): void {
    isOpen.value = false
    pendingPrompt.value = null
    pendingMessage.value = null
    config.value = {}
  }

  return {
    config: readonly(config),
    isOpen: readonly(isOpen),
    conversationKey: readonly(conversationKey),
    openEventKey: readonly(openEventKey),
    expandKey: readonly(expandKey),
    pendingPrompt: shallowReadonly(pendingPrompt),
    pendingMessage: shallowReadonly(pendingMessage),
    openAiChatCard,
    sendPrompt,
    sendMessage,
    consumePendingPrompt,
    consumePendingMessage,
    closeAiChatCard,
  }
}
